import React, { useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import { Box, Button, Chip, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';

import CopyableField from '~/components/Common/CopyableField';

import useCurrentUser from '~/hooks/useCurrentUser';

import { canManageInfiniteMember } from '~/utils/luxLoyalty';

import BookingAuthorityOtpDialog from './BookingAuthorityOtpDialog';
import InfiniteLinkedMembersSection from './InfiniteLinkedMembersSection';
import InfiniteProfileEditDialog from './InfiniteProfileEditDialog';
import { getAddress, getDisplayName, getPhone } from './LuxLoyaltyInfiniteProfileDetails.utils';
import { useInfiniteLinkedMembers } from './useInfiniteLinkedMembers';

function DetailField({
  label,
  value,
  copyValue,
}: {
  label: string;
  value: React.ReactNode;
  copyValue?: string | number | null;
}) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {copyValue ? (
        <CopyableField variant="body1" value={copyValue} label={value} />
      ) : typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body1">{value}</Typography>
      ) : (
        value
      )}
    </Box>
  );
}

export default function LuxLoyaltyInfiniteProfileDetails({
  profile,
  onProfileUpdated,
  framed = true,
}: {
  profile: App.LuxLoyaltyInfiniteProfile | null | undefined;
  onProfileUpdated?: () => Promise<void> | void;
  framed?: boolean;
}) {
  const { user } = useCurrentUser();
  const [otpAuthority, setOtpAuthority] = useState<App.LuxLoyaltyInfiniteBookingAuthority | null>(null);
  const [verifiedAuthorityIds, setVerifiedAuthorityIds] = useState<Array<string>>([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const linkedMembers = useInfiniteLinkedMembers(profile);

  if (!profile) {
    return null;
  }

  const bookingAuthorities = profile.bookingAuthorities ?? [];
  const isPrimaryProfile = profile.role === 'PRIMARY';
  const profileName = getDisplayName(profile);
  const profilePhone = getPhone(profile);
  const profileAddress = getAddress(profile) || '-';
  const canEditInfiniteProfile = canManageInfiniteMember(user);
  const applicationUrl = profile.applicationId
    ? `/societe/infinite?applicationId=${encodeURIComponent(profile.applicationId)}`
    : null;

  const content = (
    <>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flex: 1 }}>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 1, color: 'text.secondary', display: 'block', fontWeight: 700 }}
            >
              Infinite profile
            </Typography>
            <Chip size="small" color="success" label={profile.role} />
            {profile.relationship && <Chip size="small" variant="outlined" label={profile.relationship} />}
          </Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setIsEditOpen(true)}
            disabled={!canEditInfiniteProfile}
          >
            Edit Infinite profile
          </Button>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <DetailField label="Profile name" value={profileName} copyValue={profileName} />
          <DetailField
            label="Email"
            value={
              applicationUrl ? (
                <MuiLink component={Link} to={applicationUrl}>
                  {profile.email}
                </MuiLink>
              ) : (
                profile.email
              )
            }
            copyValue={profile.email}
          />
          <DetailField label="Phone" value={profilePhone} copyValue={profilePhone} />
          {isPrimaryProfile && <DetailField label="Address" value={profileAddress} copyValue={profileAddress} />}
        </Box>

        <InfiniteLinkedMembersSection
          isPrimaryProfile={linkedMembers.isPrimaryProfile}
          members={linkedMembers.members}
          isLoading={linkedMembers.isLoading}
          isError={linkedMembers.isError}
        />

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Booking authorities ({bookingAuthorities.length})
          </Typography>
          {bookingAuthorities.length > 0 ? (
            <Stack spacing={1}>
              {bookingAuthorities.map((authority, index) => (
                <Paper key={authority.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" variant="outlined" label={`Booking authority ${index + 1}`} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {[authority.title, authority.firstName, authority.lastName].filter(Boolean).join(' ')}
                        </Typography>
                        {authority.isDeactivated && <Chip size="small" color="default" label="Deactivated" />}
                        {verifiedAuthorityIds.includes(authority.id) && (
                          <Chip size="small" color="success" label="Phone verified" />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {authority.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getPhone(authority)}
                      </Typography>
                    </Box>
                    <Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setOtpAuthority(authority)}
                        disabled={!!authority.isDeactivated}
                      >
                        Verify booking authority phone
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No booking authorities linked.
            </Typography>
          )}
        </Box>

        {isPrimaryProfile && profile.welcomePackNotes && (
          <DetailField
            label="Welcome pack notes"
            value={
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {profile.welcomePackNotes}
              </Typography>
            }
            copyValue={profile.welcomePackNotes}
          />
        )}
      </Stack>
      <BookingAuthorityOtpDialog
        authority={otpAuthority}
        open={Boolean(otpAuthority)}
        onClose={() => setOtpAuthority(null)}
        onVerified={(authorityId) =>
          setVerifiedAuthorityIds((current) => (current.includes(authorityId) ? current : [...current, authorityId]))
        }
      />
      <InfiniteProfileEditDialog
        profile={profile}
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSaved={async () => {
          await onProfileUpdated?.();
        }}
      />
    </>
  );

  if (!framed) {
    return content;
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
      {content}
    </Paper>
  );
}
